import { useCallback, useEffect } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Tabs, Text } from '@frontend/design-system';
import { labelMap, labelMapKeys, translationNamespace } from '../constants';
import { useLocationPicker } from '../store';
import { LocationPickerTrackingIds } from '../tracking-ids';
import { LocationPickerSearchFilters } from '../types';

export type LocationSearchFilterProps = {
  count: {
    [K in LocationPickerSearchFilters]: number;
  };
};

export const LocationSearchFilter = ({ count }: LocationSearchFilterProps) => {
  const { activeSearchFilter, setActiveSearchFilter, hasSingleLocationGroup, hasSingleOrganization } =
    useLocationPicker([
      'activeSearchFilter',
      'setActiveSearchFilter',
      'hasSingleLocationGroup',
      'hasSingleOrganization',
    ]);
  const { t } = useTranslation(translationNamespace);

  useEffect(() => {
    if (count.locations > 0) {
      setActiveSearchFilter('locations');
      return;
    }

    if (count.locationGroups > 0 && !hasSingleLocationGroup) {
      setActiveSearchFilter('locationGroups');
      return;
    }

    if (count.organizations > 0 && !hasSingleOrganization) {
      setActiveSearchFilter('organizations');
      return;
    }
  }, [count]);

  const handleTabChange = useCallback(
    (tabId: string) => {
      setActiveSearchFilter(tabId as LocationPickerSearchFilters);
    },
    [setActiveSearchFilter]
  );

  if (hasSingleLocationGroup && hasSingleOrganization) {
    return null;
  }

  return (
    <Tabs initialTab={activeSearchFilter} onChange={handleTabChange}>
      <Tabs.Bar fullWidth css={{ marginTop: theme.spacing(1) }}>
        {labelMapKeys.map((key) => {
          if (key === 'locationGroups' && hasSingleLocationGroup) {
            return null;
          }

          if (key === 'organizations' && hasSingleOrganization) {
            return null;
          }

          return (
            <Tabs.Tab
              trackingId={LocationPickerTrackingIds.searchFilters(key)}
              css={css`
                white-space: nowrap;
                display: flex;
              `}
              key={key}
              id={key}
            >
              <Text
                css={css`
                  font-size: inherit;
                  font-weight: inherit;
                  position: relative;
                `}
              >
                <span>{t(labelMap[key])}</span>
                {!!count[key] && (
                  <Text
                    as='span'
                    weight='bold'
                    color='subdued'
                    css={css`
                      display: inline-block;
                      font-size: inherit;
                    `}
                  >
                    &nbsp;&nbsp; {count[key]}
                  </Text>
                )}
              </Text>
            </Tabs.Tab>
          );
        })}
      </Tabs.Bar>
    </Tabs>
  );
};
